import { useSelector } from 'react-redux';

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { brands, useBrand } from '@atc/brand';

import getVehicleHighlightWithFallback from '@/utilities/getVehicleHighlightWithFallback';

import authDuck from '@/ducks/authDuck.js';
import cpoContentDuck from '@/ducks/cpoContentDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpLocationTypeDuck from '@/ducks/srp/srpLocationTypeDuck.js';
import srpNewCarBoostDuck from '@/ducks/srp/srpNewCarBoostDuck.js';
import srpPaginationDuck from '@/ducks/srp/srpPaginationDuck.js';
import srpPrimeSpotlightDuck from '@/ducks/srp/srpPrimeSpotlightDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';
import srpSpotlightDuck from '@/ducks/srp/srpSpotlightDuck.js';
import srpSupplementalDuck from '@/ducks/srp/srpSupplementalDuck.js';
import userDuck from '@/ducks/userDuck.js';

/**
 * Custom hook to consolidate all Redux selectors and derived state for Search Results Page
 *
 * @returns {Object} Consolidated search results data
 * @property {Object} brandData - Brand information and branding flags
 * @property {Object} deviceData - Device size flags (isLg, isXs, isSm)
 * @property {Object} listingsData - Primary listings data
 * @property {Object} spotlightData - Spotlight listings data
 * @property {Object} supplementalData - Supplemental search results data
 * @property {Object} paginationData - Pagination state
 * @property {Object} userData - User authentication and profile data
 * @property {Object} filtersData - Active search filters
 * @property {Object} derivedState - Computed values from multiple sources
 */
export function useSearchResultsData() {
    const device = useDevice();
    const { brand, isBrand } = useBrand();

    // Brand data
    const brandData = {
        brand,
        isBrand,
        isKbbBranded: isBrand(brands.KBB_BRAND),
        isFBABranded: isBrand(brands.FORD_BRAND),
    };

    // Device data
    const deviceData = {
        device,
        isLg: _get(device, 'is.lg', true),
        isXs: _get(device, 'is.xs', false),
        isSm: _get(device, 'is.sm', false),
    };

    // Filters data
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    const filtersData = {
        zip: filtersValues.zip,
        searchRadius: useSelector(srpFiltersDuck.selectors.getSelectedSearchRadius),
        isElectricFilter: useSelector(srpFiltersDuck.selectors.getFuelTypeValues).includes('ELE'),
    };

    // Primary listings data
    const listings = useSelector((state) => srpResultsDuck.selectors.getActiveInventory(state));
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);
    const isResultsLoading = useSelector(srpResultsDuck.selectors.getResultLoading);
    const isMaxActiveResults = useSelector(srpResultsDuck.selectors.getIsMaxActiveResults);

    const listingsData = {
        listings,
        resultCount,
        isResultsLoading,
        isMaxActiveResults,
    };

    // Spotlight data
    const spotlights = useSelector(srpSpotlightDuck.selectors.getActiveInventory, _isEqual);
    const spotlightTypes = useSelector(srpSpotlightDuck.selectors.getSpotlightTypes, _isEqual);
    const boostListings = useSelector(srpNewCarBoostDuck.selectors.getAvailableInventory, _isEqual);
    const vehicleHighlightListings = useSelector(srpPrimeSpotlightDuck.selectors.getActiveInventory, _isEqual);

    const {
        vehicleHighlightListingsFinal = [],
        vehicleHighlightCount = 0,
    } = getVehicleHighlightWithFallback(vehicleHighlightListings);

    // Split vehicle highlights into peekaboo listing and carousel listings
    const [vehicleHighlightBooListing, ...rest] = vehicleHighlightListingsFinal;
    const vehicleHighlightCarouselListings = vehicleHighlightCount === 1
        ? vehicleHighlightListingsFinal
        : rest;

    const spotlightData = {
        spotlights,
        spotlightTypes,
        boostListings,
        vehicleHighlightListings,
        vehicleHighlightBooListing,
        vehicleHighlightCarouselListings,
        vehicleHighlightCount,
    };

    // Supplemental data
    const supplementalData = {
        supplementalResultsCount: useSelector(srpSupplementalDuck.selectors.getResultCount),
        supplementalResultsLoaded: useSelector(srpSupplementalDuck.selectors.getLoadedCount),
        supplementalInitialLoad: useSelector(srpSupplementalDuck.selectors.getInitialLoad),
        supplementalLoading: useSelector(srpSupplementalDuck.selectors.getLoading),
    };

    // Pagination data
    const currentPage = useSelector(srpPaginationDuck.selectors.getCurrentPage);
    let resultsPerPage = useSelector(srpPaginationDuck.selectors.getNumRecords);

    // Brand-specific pagination adjustment
    if (brandData.isFBABranded && resultsPerPage === 25) {
        resultsPerPage = 30;
    }

    const isLastPage = currentPage >= Math.ceil(resultCount / resultsPerPage);

    const paginationData = {
        currentPage,
        resultsPerPage,
        isLastPage,
    };

    // User data
    const userData = {
        isLoggedIn: useSelector(authDuck.selectors.isLoggedIn),
        leadProfile: useSelector(userDuck.selectors.getLeadProfile),
        disableLazyListings: useSelector(srpActiveInteractionDuck.selectors.getDisableLazyListings),
    };

    // Other data
    const otherData = {
        sort: useSelector(srpSortDuck.selectors.getDuckState),
        query: useSelector(queryDuck.selectors.getDuckState),
        certifiedSponsor: useSelector(cpoContentDuck.selectors.selectCertifiedContentSimple),
        isNationalSRP: useSelector(srpLocationTypeDuck.selectors.getDuckState),
    };

    // Derived state
    const hasSearchRadius = !!filtersData.searchRadius;
    const isRelevantSort = otherData.sort === 'relevance';
    const showSupplementalResults = isLastPage && hasSearchRadius;

    const derivedState = {
        hasSearchRadius,
        isRelevantSort,
        showSupplementalResults,
    };

    return {
        brandData,
        deviceData,
        listingsData,
        spotlightData,
        supplementalData,
        paginationData,
        userData,
        filtersData,
        ...otherData,
        derivedState,
    };
}
